import { Box, Menu, MenuItem, Typography } from '@mui/material'
import { MRT_Row } from 'material-react-table'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ALERT_STATUS, UserPermissions } from '../../../../constants/constants'
import { useAlertTypeOptions } from '../../../../hooks/useAlertTypeOptions'
import { useAppSelector } from '../../../../redux/hook'
import MRTTable from '../../../Common/Table'
import AlertTimelinePanel from './AlertTimelinePanel'
import { buildAlertsColumns } from './alertsColumns'
import { Alert, AlertsApiResponse } from './interfaces'
import { useAlertNotes } from './useAlertNotes'
import { useAlertsData } from './useAlertsData'

interface AlertsPropsType {
    orgId: string
    entityId: string
    showStatus?: boolean
}

/* -------------------------------- component ------------------------------- */

const Alerts = ({
    orgId,
    entityId,
    showStatus = false,
}: AlertsPropsType): ReactElement => {
    const { t } = useTranslation()
    const ALERT_TYPE_OPTIONS = useAlertTypeOptions()

    const user = useAppSelector((state) => state.user.user)
    const permissions = useAppSelector((state) => state.org.permissions)

    const [statusMenuAnchor, setStatusMenuAnchor] = useState<{
        el: HTMLElement
        row: Alert
    } | null>(null)

    const canWrite = useMemo(() => {
        return (
            permissions.includes(UserPermissions.ORG_ADMIN) ||
            permissions.includes(UserPermissions.ENTITY_WRITER) ||
            permissions.includes(UserPermissions.DEVICE_WRITER) ||
            permissions.includes(UserPermissions.ENTITY_ANALYST) ||
            permissions.includes(UserPermissions.USER_ADMIN) ||
            permissions.includes(UserPermissions.USER_USERORGS)
        )
    }, [permissions])

    const {
        flatData,
        totalDBRowCount,
        fetchNextPage,
        isError,
        isFetching,
        isLoading,
        updateStatus,
        queryKey,
    } = useAlertsData(orgId, entityId)

    const notes = useAlertNotes(orgId, queryKey, canWrite)

    const handleStatusMenuClick = useCallback(
        (e: React.MouseEvent<HTMLElement>, alert: Alert) => {
            e.stopPropagation()
            setStatusMenuAnchor({
                el: e.currentTarget,
                row: alert,
            })
        },
        [],
    )

    const columns = useMemo(
        () =>
            buildAlertsColumns({
                t,
                showStatus,
                user,
                canWrite,
                updateStatus,
                handleStatusMenuClick,
                alertTypeOptions: ALERT_TYPE_OPTIONS,
            }),
        [
            t,
            showStatus,
            user,
            canWrite,
            updateStatus,
            handleStatusMenuClick,
            ALERT_TYPE_OPTIONS,
        ],
    )

    /* -------------------------------- render -------------------------------- */

    return (
        <>
            <Box sx={{ background: '#DCECF9', p: 2, mt: 2 }}>
                <Typography
                    variant="h6"
                    sx={{ fontSize: '18px', fontWeight: 600 }}
                >
                    {t('alerts')}
                </Typography>
            </Box>

            <MRTTable<Alert, AlertsApiResponse>
                columns={columns}
                sorting={[]}
                globalFilter={''}
                columnFilters={[]}
                setColumnFilters={() => {}}
                setGlobalFilter={() => {}}
                setSorting={() => {}}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableRowSelection={false}
                enableSorting={false}
                enableBottomToolbar={false}
                isCurvedBottomToolbar={false}
                maxHeight="600px"
                enableExpanding
                initialState={{
                    expanded: true,
                    columnVisibility: { 'mrt-row-expand': false },
                }}
                displayColumnDefOptions={{
                    'mrt-row-expand': { size: 0 },
                }}
                muiDetailPanelProps={{ sx: { padding: 0 } }}
                columnMaxWidth={600}
                renderDetailPanel={({ row }: { row: MRT_Row<Alert> }) =>
                    showStatus ? (
                        <AlertTimelinePanel
                            alert={row.original}
                            canWrite={canWrite}
                            userLocalization={user?.preferences?.localization}
                            notes={notes}
                        />
                    ) : null
                }
            />

            {/* Status Dropdown Menu */}
            <Menu
                anchorEl={statusMenuAnchor?.el}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {statusMenuAnchor?.row?.status !==
                    ALERT_STATUS.ACKNOWLEDGED && (
                    <MenuItem
                        id="alert-status-acknowledge-menu-item"
                        onClick={() => {
                            updateStatus(
                                statusMenuAnchor!.row,
                                ALERT_STATUS.ACKNOWLEDGED,
                            )
                            setStatusMenuAnchor(null)
                        }}
                    >
                        {t('Mark as Acknowledged (Read)')}
                    </MenuItem>
                )}
                {statusMenuAnchor?.row?.status !== ALERT_STATUS.IGNORED && (
                    <MenuItem
                        id="alert-status-ignore-menu-item"
                        onClick={() => {
                            updateStatus(
                                statusMenuAnchor!.row,
                                ALERT_STATUS.IGNORED,
                            )
                            setStatusMenuAnchor(null)
                        }}
                    >
                        {t('Mark as Ignored')}
                    </MenuItem>
                )}
                {statusMenuAnchor?.row?.status !== ALERT_STATUS.UNREAD && (
                    <MenuItem
                        id="alert-status-mark-as-unread-menu-item"
                        onClick={() => {
                            updateStatus(
                                statusMenuAnchor!.row,
                                ALERT_STATUS.UNREAD,
                            )
                            setStatusMenuAnchor(null)
                        }}
                    >
                        {t('Mark as New (Unread)')}
                    </MenuItem>
                )}
            </Menu>
        </>
    )
}

export default Alerts
